// CloudSaveModal — модалка «Сохранить в облако» с полями name/description
// и галочкой публикации в Ленту. Также генерирует thumbnail из canvas.
//
// Использование: store.cloudSaveModalOpen=true → модалка открывается.
// При сохранении формирует thumbnail через canvas.toDataURL и обновляет
// project через KubikonCadService.

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useCadStore from './store/useCadStore';
import { getCurrentUserId } from './cloudIO';
import { serializeDocument } from './documentIO';
import * as Cad from '../../API/KubikonCadService';
import cl from './CloudSaveModal.module.css';


// Захват thumbnail из главного 3D-canvas. Размер ~256x160 (16:10).
// Three.js canvas использует WebGL — для toDataURL нужно успеть до того
// как буфер очистится. Создаём fake render-frame через requestAnimationFrame.
function captureCanvasThumbnail() {
    return new Promise((resolve) => {
        // Ищем canvas внутри сцены — он создан react-three-fiber.
        const canvases = document.querySelectorAll('canvas');
        // Берём самый большой canvas (главный viewport, не ViewCube 110x110).
        let best = null;
        let bestArea = 0;
        for (const c of canvases) {
            const area = c.width * c.height;
            if (area > bestArea) { best = c; bestArea = area; }
        }
        if (!best) { resolve(''); return; }
        // Делаем reduced-копию через offscreen canvas
        try {
            const tw = 320, th = 200;
            const off = document.createElement('canvas');
            off.width = tw;
            off.height = th;
            const ctx = off.getContext('2d');
            // Cover-fit
            const srcRatio = best.width / best.height;
            const dstRatio = tw / th;
            let sx, sy, sw, sh;
            if (srcRatio > dstRatio) {
                sh = best.height;
                sw = sh * dstRatio;
                sx = (best.width - sw) / 2;
                sy = 0;
            } else {
                sw = best.width;
                sh = sw / dstRatio;
                sx = 0;
                sy = (best.height - sh) / 2;
            }
            ctx.fillStyle = '#f4f6fa';
            ctx.fillRect(0, 0, tw, th);
            ctx.drawImage(best, sx, sy, sw, sh, 0, 0, tw, th);
            resolve(off.toDataURL('image/jpeg', 0.7));
        } catch (e) {
            console.warn('[CloudSaveModal] thumbnail capture failed:', e);
            resolve('');
        }
    });
}


const CloudSaveModal = () => {
    const navigate = useNavigate();
    const open = useCadStore((s) => s.cloudSaveModalOpen);
    const setOpen = useCadStore((s) => s.setCloudSaveModalOpen);
    const cloudProjectId = useCadStore((s) => s.cloudProjectId);
    const setCloudProjectId = useCadStore((s) => s.setCloudProjectId);
    const markSaved = useCadStore((s) => s.markSaved);
    const docTitle = useCadStore((s) => s.doc?.title);

    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [isPublic, setIsPublic] = useState(false);
    const [thumbnail, setThumbnail] = useState('');
    const [saving, setSaving] = useState(false);

    // При открытии — подтягиваем текущие значения из проекта
    useEffect(() => {
        if (!open) return;
        let cancelled = false;
        (async () => {
            const uid = getCurrentUserId();
            // Если есть cloudProjectId — подгружаем текущие title/description/is_public
            if (cloudProjectId && uid) {
                try {
                    const res = await Cad.getProject(cloudProjectId, uid);
                    if (cancelled) return;
                    setTitle(res.data?.title || docTitle || 'Без названия');
                    setDescription(res.data?.description || '');
                    setIsPublic(!!res.data?.is_public);
                } catch (_) {
                    setTitle(docTitle || 'Без названия');
                }
            } else {
                setTitle(docTitle || 'Без названия');
                setDescription('');
                setIsPublic(false);
            }
            // Захватываем thumbnail
            const thumb = await captureCanvasThumbnail();
            if (!cancelled) setThumbnail(thumb);
        })();
        return () => { cancelled = true; };
    }, [open, cloudProjectId, docTitle]);

    if (!open) return null;

    const onClose = () => setOpen(false);

    const onSave = async () => {
        const uid = getCurrentUserId();
        if (!uid) { alert('Войди в аккаунт чтобы сохранить'); return; }
        if (!title.trim()) { alert('Введи название проекта'); return; }
        setSaving(true);
        try {
            const doc = serializeDocument();
            doc.title = title.trim();
            const body = {
                user_id: uid,
                title: title.trim().slice(0, 200),
                description: description.trim().slice(0, 2000),
                document_data: JSON.stringify(doc),
                thumbnail,
                is_public: isPublic,
            };
            if (cloudProjectId) {
                await Cad.updateProject(cloudProjectId, body);
            } else {
                const r = await Cad.createProject(body);
                const newId = r.data.id;
                setCloudProjectId(newId);
                // После первого create перенаправляем на /:id, чтобы F5 открывал
                // именно этот проект (а не /new).
                navigate(`/main/cad-editor/${newId}`, { replace: true });
            }
            markSaved();
            setOpen(false);
        } catch (e) {
            console.error('[CloudSaveModal] save failed:', e);
            alert('Ошибка сохранения: ' + (e?.response?.data?.error || e.message || e));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className={cl.overlay} onClick={onClose}>
            <div className={cl.modal} onClick={(e) => e.stopPropagation()}>
                <div className={cl.header}>
                    <h2 className={cl.title}>
                        {cloudProjectId ? '☁ Сохранить изменения' : '☁ Сохранить в облако'}
                    </h2>
                    <button className={cl.closeBtn} onClick={onClose} title="Закрыть">✕</button>
                </div>

                <div className={cl.body}>
                    {thumbnail && (
                        <div className={cl.previewBox}>
                            <img src={thumbnail} alt="превью" className={cl.previewImg}/>
                            <div className={cl.previewLabel}>Превью</div>
                        </div>
                    )}

                    <div className={cl.field}>
                        <label className={cl.label}>Название</label>
                        <input
                            type="text"
                            className={cl.input}
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder="Например: Корпус для Arduino"
                            maxLength={200}
                            autoFocus
                        />
                    </div>

                    <div className={cl.field}>
                        <label className={cl.label}>Описание</label>
                        <textarea
                            className={cl.textarea}
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="Опиши что это и для чего..."
                            maxLength={2000}
                            rows={4}
                        />
                    </div>

                    <div className={cl.checkboxField}>
                        <label className={cl.checkboxLabel}>
                            <input
                                type="checkbox"
                                checked={isPublic}
                                onChange={(e) => setIsPublic(e.target.checked)}
                            />
                            <span className={cl.checkboxText}>
                                <strong>🌍 Опубликовать в Ленте сообщества</strong>
                                <small>
                                    Другие смогут увидеть, копировать и комментировать твой проект.
                                </small>
                            </span>
                        </label>
                    </div>
                </div>

                <div className={cl.footer}>
                    <button
                        className={`${cl.btn} ${cl.btnSecondary}`}
                        onClick={onClose}
                        disabled={saving}
                    >
                        Отмена
                    </button>
                    <button
                        className={`${cl.btn} ${cl.btnPrimary}`}
                        onClick={onSave}
                        disabled={saving || !title.trim()}
                    >
                        {saving ? 'Сохранение...' : (cloudProjectId ? 'Сохранить' : 'Создать проект')}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CloudSaveModal;
